// "Completar con IA" de la ficha — ADR-2026-09-08-ficha-con-ia.
//
// Lo que vuelve es una SUGERENCIA, no un guardado. La pantalla la muestra al
// lado de lo que hay y el usuario decide: el peso y las medidas deciden lo que
// cobra el correo, y un número inventado que entra solo a la ficha se publica
// sin que nadie lo haya mirado.
import { atlasBackendFetch } from '../../lib/atlasBackend';
import { reglasDe, type CanalLanzamiento } from './canales';

/** Qué se le puede pedir. Mismo vocabulario que el backend. */
export const CAMPOS_IA = ['descripcion', 'marca', 'tags', 'seo', 'categorias', 'envio'] as const;
export type CampoIa = (typeof CAMPOS_IA)[number];

/**
 * Qué se le puede pedir a la IA en ESTE destino.
 *
 * Un destino que sólo exporta carpetas no tiene descripción, marca, SEO ni
 * envío: la ficha no los muestra (ADR-2026-09-14-lanzamiento-a-carpetas), así
 * que ofrecer completarlos sería gastar una llamada al modelo para guardar algo
 * que nadie va a ver. Queda la categoría, que ahí es más importante que nunca:
 * es la carpeta donde van a caer las fotos.
 */
export function camposParaCanal(canal: CanalLanzamiento | null | undefined): CampoIa[] {
  const r = reglasDe(canal ?? 'tiendanube');
  return CAMPOS_IA.filter((c) => {
    if (c === 'categorias') return true;
    if (c === 'envio') return r.conEnvio;
    return r.conMarcaYSeo;
  });
}

/** Cómo se llama cada uno en pantalla. */
export const NOMBRE_DE_CAMPO: Record<CampoIa, string> = {
  descripcion: 'Descripción',
  marca: 'Marca',
  tags: 'Etiquetas',
  seo: 'SEO',
  categorias: 'Categorías',
  envio: 'Peso y medidas',
};

export interface SugerenciaDeFicha {
  descripcion?: string;
  marca?: string;
  tags?: string;
  seoTitulo?: string;
  seoDescripcion?: string;
  categoriaIds?: string[];
  /** Caminos a CREAR, desde la raíz: ["Remeras", "Oversize"]. Lo que ya exista se reusa. */
  categoriasNuevas?: string[][];
  peso?: string;
  alto?: string;
  ancho?: string;
  profundidad?: string;
  /** Cómo empacó el producto para sacar las medidas. Es lo que deja juzgarlas. */
  razonMedidas?: string;
}

export interface PedidoDeFicha {
  nombre: string;
  descripcion?: string | null;
  rubro?: string | null;
  marca?: string | null;
  variantes?: string[];
  categorias: Array<{ id: string; camino: string }>;
  campos: CampoIa[];
  /** Nombres de archivo de sus fotos. El backend las baja con SU id de empresa. */
  fileNames: string[];
  /**
   * A dónde va el lanzamiento. El backend escribe el SEO con los topes de ESE
   * destino y, en VTEX, elige una sola categoría
   * (ADR-2026-09-14-la-ia-escribe-el-seo-con-los-topes-del-destino).
   */
  canal?: CanalLanzamiento;
}

/** Un pedido con fotos y razonamiento puede tardar; después no vale la pena esperar. */
const TIMEOUT_MS = 90_000;

export async function completarFichaConIa(
  p: PedidoDeFicha,
): Promise<{ sugerencia: SugerenciaDeFicha | null; error: string | null }> {
  const ctrl = new AbortController();
  const reloj = window.setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await atlasBackendFetch(
      '/v1/products/ficha-ia',
      {
        nombre: p.nombre,
        descripcion: p.descripcion || undefined,
        rubro: p.rubro || undefined,
        marca: p.marca || undefined,
        variantes: p.variantes?.length ? p.variantes : undefined,
        categorias: p.categorias,
        campos: p.campos,
        canal: p.canal,
        file_names: p.fileNames,
      },
      { signal: ctrl.signal },
    );
    const json = (await res.json().catch(() => null)) as
      | { ok?: boolean; sugerencia?: SugerenciaDeFicha; error?: string }
      | null;
    if (!res.ok || !json?.ok || !json.sugerencia) {
      return { sugerencia: null, error: json?.error || 'No se pudo completar con IA' };
    }
    return { sugerencia: json.sugerencia, error: null };
  } catch (e) {
    const abortada = e instanceof DOMException && e.name === 'AbortError';
    return {
      sugerencia: null,
      error: abortada ? 'La IA tardó demasiado. Probá de nuevo.' : 'No se pudo completar con IA',
    };
  } finally {
    window.clearTimeout(reloj);
  }
}

/** Si la sugerencia trajo algo para este campo. */
export function tieneAlgo(s: SugerenciaDeFicha, campo: CampoIa): boolean {
  switch (campo) {
    case 'descripcion':
      return !!s.descripcion;
    case 'marca':
      return !!s.marca;
    case 'tags':
      return !!s.tags;
    case 'seo':
      return !!s.seoTitulo || !!s.seoDescripcion;
    case 'categorias':
      return !!s.categoriaIds?.length || !!s.categoriasNuevas?.length;
    case 'envio':
      return !!s.peso || !!s.alto;
  }
}

/**
 * Qué va diciendo el botón mientras la IA trabaja.
 *
 * Sale de los campos que el usuario PIDIÓ, no de una lista fija: decir "pensando
 * la descripción" cuando no se pidió descripción es mentirle sobre lo que está
 * pasando, y una espera que miente se nota más que una que no dice nada.
 *
 * Empieza y termina igual siempre porque esas dos sí son verdad en todos los
 * casos: primero mira las fotos, al final arma la respuesta.
 */
const MENSAJE_DE_CAMPO: Record<CampoIa, string> = {
  descripcion: 'Pensando la descripción…',
  marca: 'Viendo de qué marca es…',
  tags: 'Buscando las etiquetas…',
  seo: 'Escribiendo el SEO…',
  categorias: 'Buscando dónde va en la tienda…',
  envio: 'Empacándolo para calcular el envío…',
};

export function mensajesDeEspera(campos: readonly CampoIa[]): string[] {
  const delMedio = CAMPOS_IA.filter((c) => campos.includes(c)).map((c) => MENSAJE_DE_CAMPO[c]);
  return ['Mirando las fotos…', ...delMedio, 'Ordenando todo…'];
}
